/* eslint-disable @typescript-eslint/dot-notation */
import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { trackEvent, SignUpStep, EventType } from 'libs/utils/src/lib/trackEvent';

import { SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { getPlatformLinkUsingRouter, getSignupRedirectLink, useHubSwitch, useUserSettings } from '@bpm-web-app/utils';
import { Account, AccountPageProps } from '../../account';
import { PageHeader } from '../../components/page-header/page-header';
import accountStyles from '../../account.module.css';
import { Card } from '../card/card';
import { BillingPeriodToggle } from '../billing-period-toggle';
import { StaticContent } from './static-content/static-content';
import { CreditsBlock } from './credits-block/credits-block';
import { featuresData } from './use-library-data';
import { usePlans } from './use-plans';
import styles from '../plans.module.css';
import { useBillingPeriod } from './use-billing-period';

type Props = AccountPageProps;

type GroupedPlans = {
    [k in SubscriptionPackage.MethodEnum]: SubscriptionPackage[];
};

export const AccountPlansCreate: FC<Props> = ({ title }) => {
    const { packages } = usePlans('create');
    const { data: currentSubscription } = useGetMeSubscription('create');
    const { isCreate } = useHubSwitch();
    const { isAnonymous } = useUserSettings();
    const [toggleDisabled, setToggleDisabled] = useState(false);
    const { data: userSubscription } = useGetMeSubscription(isCreate ? 'create' : 'supreme', false);
    const [period, setPeriod] = useBillingPeriod();

    // Groups the array of packages by value of "method" property
    const groupedData: GroupedPlans = packages
        ? // eslint-disable-next-line no-return-assign, no-param-reassign, no-sequences
        packages.data.reduce((r, v, _i, _a, k = v.method) => ((r[k] || (r[k] = [])).push(v), r), {} as { [k: string]: SubscriptionPackage[] })
        : {};

    const subscriptions = groupedData?.[SubscriptionPackage.MethodEnum.Recurring]?.filter((d) => {
        if (period === '1') {
            if (d.trial_period === 12) {
                return false;
            }
            if (d.period === 1) {
                return true;
            }
        }
        if (period === '12') {
            if (d.period === 12) {
                return true;
            }
            if (d.trial_period === 12) {
                return true;
            }
        }
        return false;
    })
        .sort((a, b) => Number(a.amount) - Number(b.amount));

    useEffect(() => {
        if (subscriptions && subscriptions.length === 0 && period === '1') {
            setPeriod('12');
            setToggleDisabled(true);
        } else if (subscriptions && subscriptions.length > 0 && period === '1') {
            setToggleDisabled(false);
        }
    }, [subscriptions, period, setPeriod, setToggleDisabled]);

    useEffect(() => {
        trackEvent({
            type: EventType.signUpStep,
            data: {
                platform: SubscriptionPackage.PlatformEnum.Create,
                step: SignUpStep.plansPage,
            }
        });
    }, []);

    return (
        <Account title={title} fluid>
            <div className={classNames(accountStyles['account__layout'], accountStyles['account__layout--wide'])}>
                <PageHeader title={title} />

                <div className={styles['plans__header']}>
                    <div>
                        <p className={styles['plans__description']}>
                            Each month you will earn credits according to your chosen plan.
                            If you don&rsquo;t use the credits, they will be carried over to the next month. Sample (.wav files) requires 1
                            credit, MIDI files and synth presets require 3 credits.
                        </p>
                        {userSubscription?.data.membership.is_supreme_bundle && (
                            <p className={classNames(styles['plans__description'], styles['plans__description__bundle'])}>
                                Included with your BPM Supreme subscription is a free subscription to BPM Create Premium Plus. To make changes to your subscription or cancel, please visit your <a style={{ textDecoration: 'underline' }} href={`${process.env['NEXT_PUBLIC_SUPREME_PLATFORM_URL']}d/account/plan`}>BPM Supreme Account.</a>{' '}
                                This plan is managed through BPM Supreme.
                            </p>
                        )}
                    </div>
                    <BillingPeriodToggle
                        value={period}
                        onChange={setPeriod}
                        disabled={toggleDisabled || !groupedData ||
                            !groupedData[SubscriptionPackage.MethodEnum.Recurring]?.filter((s) => (s.period === 12 || s.trial_period === 12) && s.info?.allowed !== false).length}
                    />
                </div>

                <div className={styles['plans__cards']}>
                    {subscriptions?.map((subscription) => {
                        let cardLink = isAnonymous
                            ? getSignupRedirectLink(`/account/plan/change/${subscription.id}?backRoute=${encodeURIComponent(getPlatformLinkUsingRouter('/account/overview'))}`)
                            : `./plan/change/${subscription.id}`;
                        const payNowPromo = subscription.info?.promoted && subscription.info.promoted_amount && subscription.info?.promoted_amount !== '0.00' ? `Pay $${subscription.info.promoted_amount} today` : '';

                        let periodStr = period === '1' ? 'mo' : 'yr';
                        let { amount } = subscription;
                        let subTitle: string | undefined;
                        let strikeThroughText: string | undefined;
                        if (subscription.info?.trial_allowed && subscription.trial_period && subscription.trial_amount) {
                            periodStr = `${subscription.trial_period === 12 ? 'One Year' : subscription.trial_period === 1 ? 'First Month' : `First ${subscription.trial_period} Months`}`;
                            strikeThroughText = `$${Number(subscription.info.trial_discount)}/${periodStr}`;
                            amount = subscription.trial_amount;
                            subTitle = `*$${subscription.amount}/mo thereafter`;
                        }
                        let customAmountText: React.ReactNode | undefined;
                        if (subscription.is_current && currentSubscription?.data.membership.is_supreme_bundle) {
                            cardLink = `${process.env['NEXT_PUBLIC_SUPREME_PLATFORM_URL']}d/account/plan`;
                            customAmountText = <>Free <span>with BPM Supreme</span></>;
                        }
                        return (
                            <Card
                                subscriptionPackage={subscription}
                                amount={amount}
                                customAmountText={customAmountText}
                                strikeThroughText={strikeThroughText}
                                subTitle={subTitle}
                                currentMembership={currentSubscription?.data.membership}
                                key={subscription.id}
                                disabled={subscription.is_current && !currentSubscription?.data.membership.is_supreme_bundle}
                                period={periodStr}
                                link={cardLink}
                                title={(subscription.display_title || subscription.name).toString()}
                                theme={subscription.name.toString().toLowerCase()}
                                features={featuresData.create[subscription.name.toString().toLowerCase() as keyof typeof featuresData.create]}
                                tag={(subscription.is_promotion || subscription.is_default) ? subscription.promotion_text : payNowPromo}
                            />
                        );
                    }
                    )}
                </div>

                <CreditsBlock hasSubscription={!!userSubscription?.data.membership.has_membership} creditPacks={groupedData[SubscriptionPackage.MethodEnum.OneTime]} />
            </div>

            <StaticContent
                onSelectPlan={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
            />
        </Account>
    );
};

/* eslint-enable @typescript-eslint/dot-notation */
